function normalizeOrderId(value) {
  const numeric = Number(value);
  return Number.isSafeInteger(numeric) && numeric > 0 ? numeric : null;
}

function uniqueOrderIds(values = []) {
  const seen = new Set();
  const ids = [];
  for (const value of Array.isArray(values) ? values : []) {
    const orderId = normalizeOrderId(value);
    if (orderId == null || seen.has(orderId)) continue;
    seen.add(orderId);
    ids.push(orderId);
  }
  return ids;
}

export function projectOrderIds(project = {}) {
  return uniqueOrderIds((Array.isArray(project?.orders) ? project.orders : [])
    .map(order => order?.auftrag_id));
}

export function reconcileProjectOrderSelection(selectedOrderIds = [], project = {}) {
  const visibleOrderIds = new Set(projectOrderIds(project));
  return uniqueOrderIds(selectedOrderIds).filter(orderId => visibleOrderIds.has(orderId));
}

export function toggleProjectOrderSelection(selectedOrderIds, project, orderId, checked) {
  const visibleOrderIds = new Set(projectOrderIds(project));
  const normalizedOrderId = normalizeOrderId(orderId);
  const selected = new Set(reconcileProjectOrderSelection(selectedOrderIds, project));
  if (normalizedOrderId == null || !visibleOrderIds.has(normalizedOrderId)) return [...selected];
  if (checked) selected.add(normalizedOrderId);
  else selected.delete(normalizedOrderId);
  return [...selected];
}

export function projectOrderSelectionState(selectedOrderIds = [], project = {}) {
  const visibleOrderIds = projectOrderIds(project);
  const selectedIds = reconcileProjectOrderSelection(selectedOrderIds, project);
  const selectedSet = new Set(selectedIds);
  const selectedVisibleIds = visibleOrderIds.filter(orderId => selectedSet.has(orderId));
  return {
    visibleOrderIds,
    selectedOrderIds: selectedVisibleIds,
    selectedCount: selectedVisibleIds.length,
    allSelected: visibleOrderIds.length > 0 && selectedVisibleIds.length === visibleOrderIds.length,
    partiallySelected: selectedVisibleIds.length > 0 && selectedVisibleIds.length < visibleOrderIds.length
  };
}

export function selectedProjectOrders(project = {}, selectedOrderIds = []) {
  const selectedIds = reconcileProjectOrderSelection(selectedOrderIds, project);
  const ordersById = new Map();
  for (const order of Array.isArray(project?.orders) ? project.orders : []) {
    const orderId = normalizeOrderId(order?.auftrag_id);
    if (orderId != null && !ordersById.has(orderId)) ordersById.set(orderId, order);
  }
  return selectedIds.map(orderId => ordersById.get(orderId)).filter(Boolean);
}

export async function runProjectOrderDeleteBatch(options = {}) {
  const currentOrderId = normalizeOrderId(options.currentOrderId);
  const uniqueOrders = [];
  const seen = new Set();
  for (const order of Array.isArray(options.orders) ? options.orders : []) {
    const orderId = normalizeOrderId(order?.auftrag_id);
    if (orderId == null || seen.has(orderId)) continue;
    seen.add(orderId);
    uniqueOrders.push(order);
  }
  const orderedDeletes = [
    ...uniqueOrders.filter(order => normalizeOrderId(order?.auftrag_id) !== currentOrderId),
    ...uniqueOrders.filter(order => normalizeOrderId(order?.auftrag_id) === currentOrderId)
  ];
  const deletedIds = [];
  const failed = [];
  for (const order of orderedDeletes) {
    const orderId = normalizeOrderId(order?.auftrag_id);
    try {
      if (orderId === currentOrderId) await options.deleteCurrentOrder(order);
      else await options.deleteOrder(order);
      deletedIds.push(orderId);
    } catch (error) {
      failed.push({
        orderId,
        orderNumber: String(order?.auftragsnummer || orderId),
        message: error?.message || 'Unbekannter Fehler'
      });
    }
  }
  return { deletedIds, failed };
}
